import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, Target, Compass, Rocket, Handshake, Leaf, Globe2, ShieldCheck, Lightbulb } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    IMG_MISSION_HERO,
    IMG_VALUE_EXCELLENCE,
    IMG_VALUE_INNOVATION,
    IMG_VALUE_PARTNERSHIP,
} from '../lib/imagery';
import { useBrandImage } from '../lib/BrandImagesContext';
import { useJsonLd, breadcrumbSchema, siteOrigin } from '../lib/seo';

const VISION_PILLARS = [
    { icon: Globe2,     title: 'Global trust and impact',              body: 'Become a globally trusted leader in advanced software engineering, artificial intelligence, cloud computing, and digital-platform innovation.' },
    { icon: Rocket,     title: 'Engineering excellence',                body: 'Build secure, resilient, interoperable, and scalable systems through strong architecture, advanced programming, quality engineering, and continuous improvement.' },
    { icon: Handshake,  title: 'Strategic partnerships',                body: 'Build long-term partnerships that combine technology, sector knowledge, local intelligence, knowledge transfer, and shared accountability for outcomes.' },
    { icon: Leaf,       title: 'Responsible and inclusive innovation',  body: 'Apply technology responsibly while advancing security, privacy, human oversight, sustainability, accessibility, and inclusion across our work.' },
];

const VALUES = [
    { key: 'excellence',  icon: Rocket,     slot: 'mission.value.excellence',  imageDefault: IMG_VALUE_EXCELLENCE,  title: 'Engineering Excellence',   body: 'Secure, maintainable, and scalable systems designed for long-term value.' },
    { key: 'innovation',  icon: Lightbulb,  slot: 'mission.value.innovation',  imageDefault: IMG_VALUE_INNOVATION,  title: 'Responsible Innovation',   body: 'Advanced technology applied with purpose, evidence, human oversight, and accountability.' },
    { key: 'partnership', icon: Handshake,  slot: 'mission.value.partnership', imageDefault: IMG_VALUE_PARTNERSHIP, title: 'Strategic Partnership',    body: 'Senior-led collaboration, transparent delivery, and meaningful knowledge transfer.' },
];

function ValueCard({ it }) {
    const src = useBrandImage(it.slot, it.imageDefault);
    return (
        <div className="dt-glass rounded-2xl overflow-hidden group hover:border-primary/40 transition-colors">
            <div className="relative aspect-[16/9] overflow-hidden">
                <img
                    src={src}
                    alt=""
                    aria-hidden
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    style={{ filter: 'saturate(0.6) hue-rotate(-6deg) brightness(0.7)' }}
                    onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[rgba(8,12,20,0.98)] via-[rgba(8,12,20,0.55)] to-[rgba(8,12,20,0.15)]" />
                <span className="absolute top-3 left-3 h-10 w-10 rounded-md bg-primary/10 border border-primary/30 inline-flex items-center justify-center text-primary backdrop-blur-md">
                    <it.icon className="h-4 w-4" />
                </span>
            </div>
            <div className="p-5">
                <div className="font-display text-lg font-semibold">{it.title}</div>
                <div className="text-sm text-muted-foreground mt-1">{it.body}</div>
            </div>
        </div>
    );
}

export default function MissionVisionPage() {
    const missionHero = useBrandImage('mission.hero', IMG_MISSION_HERO);
    const origin = siteOrigin();

    useJsonLd('mission-page', {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        '@id': `${origin}/mission#page`,
        url: `${origin}/mission`,
        name: 'Mission & Vision · DigiTech Cloud AI',
        description: 'Our mission is to engineer secure, intelligent, and scalable digital systems that strengthen institutions and communities — rooted in Africa, connected to the world.',
        inLanguage: 'en',
        about: { '@id': `${origin}/#organization` },
    });
    useJsonLd('mission-breadcrumb', breadcrumbSchema([
        { name: 'Home',              url: `${origin}/` },
        { name: 'Mission & Vision',  url: `${origin}/mission` },
    ]));

    return (
        <div data-testid="mission-page">
            {/* Hero */}
            <section className="relative pt-32 lg:pt-36 pb-16">
                <div className="absolute inset-0 -z-10 dt-grid-overlay dt-noise pointer-events-none" />
                <div
                    aria-hidden
                    className="absolute inset-0 -z-20 pointer-events-none"
                    style={{
                        backgroundImage: `url(${missionHero})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center 35%',
                        filter: 'saturate(0.55) hue-rotate(-6deg) brightness(0.55)',
                        opacity: 0.28,
                    }}
                />
                <div
                    aria-hidden
                    className="absolute inset-0 -z-20 pointer-events-none bg-gradient-to-b from-[rgba(8,12,20,0.85)] via-[rgba(8,12,20,0.7)] to-[rgba(8,12,20,1)]"
                />
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <span className="dt-section-eyebrow">PURPOSE • MISSION • VISION</span>
                    <motion.h1
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="font-display mt-4 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] max-w-4xl"
                    >
                        Engineering technology that <span className="dt-gradient-text">strengthens institutions</span>, businesses, and communities.
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.08 }}
                        className="mt-5 text-muted-foreground max-w-2xl leading-relaxed"
                    >
                        We harness advanced technology as a force for institutional performance, inclusive growth, and sustainable progress—rooted in Africa, connected to the world, and designed for lasting value.
                    </motion.p>
                </div>
            </section>

            {/* Purpose */}
            <section className="pb-10">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="dt-glass rounded-2xl p-8 sm:p-12">
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                            <div className="lg:col-span-5">
                                <div className="inline-flex items-center gap-2">
                                    <span className="h-10 w-10 rounded-xl bg-primary/10 border border-primary/30 inline-flex items-center justify-center text-primary">
                                        <Compass className="h-5 w-5" />
                                    </span>
                                    <span className="font-mono text-[11px] tracking-[0.22em] text-primary">OUR PURPOSE</span>
                                </div>
                                <h2 className="font-display mt-4 text-2xl sm:text-3xl font-semibold tracking-tight">
                                    Technology as a force for <span className="dt-gradient-text">sustainable progress</span>.
                                </h2>
                            </div>
                            <div className="lg:col-span-7">
                                <p className="text-lg text-foreground/90 leading-relaxed">
                                    To harness advanced technology as a force for institutional performance, inclusive growth, and sustainable progress.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Mission */}
            <section className="py-10">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="dt-neon-border">
                        <div className="dt-neon-inner relative overflow-hidden rounded-[var(--radius)] p-8 sm:p-12 lg:p-14">
                            <div className="absolute -top-16 -right-12 h-72 w-72 rounded-full bg-[radial-gradient(closest-side,rgba(0,229,255,0.18),transparent)] pointer-events-none" />
                            <div className="absolute -bottom-24 -left-16 h-96 w-96 rounded-full bg-[radial-gradient(closest-side,rgba(72,255,175,0.12),transparent)] pointer-events-none" />
                            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                                <div className="lg:col-span-5">
                                    <div className="inline-flex items-center gap-2">
                                        <span className="h-10 w-10 rounded-xl bg-primary/10 border border-primary/30 inline-flex items-center justify-center text-primary">
                                            <Target className="h-5 w-5" />
                                        </span>
                                        <span className="font-mono text-[11px] tracking-[0.22em] text-primary">OUR MISSION</span>
                                    </div>
                                    <h2 className="font-display mt-4 text-3xl sm:text-4xl font-semibold tracking-tight">
                                        Transforming complex challenges into <span className="dt-gradient-text">high-impact digital solutions</span>.
                                    </h2>
                                </div>
                                <div className="lg:col-span-7">
                                    <p className="text-lg sm:text-xl text-foreground/90 leading-relaxed">
                                        To transform the ICT landscape by engineering secure, intelligent, and high-impact digital solutions that solve real operational challenges and create lasting value for governments, businesses, and communities.
                                    </p>
                                    <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3">
                                        {['Engineering excellence', 'Security and responsibility', 'Measurable, lasting value'].map((b) => (
                                            <div key={b} className="rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-foreground/85">{b}</div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Vision */}
            <section className="py-16">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center gap-2 mb-4">
                        <span className="h-10 w-10 rounded-xl bg-primary/10 border border-primary/30 inline-flex items-center justify-center text-primary">
                            <Compass className="h-5 w-5" />
                        </span>
                        <span className="font-mono text-[11px] tracking-[0.22em] text-primary">OUR VISION</span>
                    </div>
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight max-w-3xl">
                        A <span className="dt-gradient-text">globally trusted</span> technology leader, rooted in Africa and connected to the world.
                    </h2>
                    <p className="mt-4 text-muted-foreground max-w-2xl leading-relaxed">
                        Our long-term direction is guided by engineering excellence, responsible innovation, strategic partnership, security, sustainability, and inclusion.
                    </p>

                    <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                        {VISION_PILLARS.map((p, i) => {
                            const Icon = p.icon;
                            return (
                                <motion.article
                                    key={p.title}
                                    initial={{ opacity: 0, y: 14 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: '-60px' }}
                                    transition={{ duration: 0.5, delay: i * 0.05 }}
                                    className="group dt-glass rounded-2xl p-6 hover:border-primary/40 transition-colors relative overflow-hidden"
                                    data-testid={`vision-pillar-${i + 1}`}
                                >
                                    <div className="absolute -top-16 -right-10 h-48 w-48 rounded-full bg-[radial-gradient(closest-side,rgba(0,229,255,0.10),transparent)] pointer-events-none" />
                                    <div className="relative flex items-start gap-4">
                                        <span className="h-11 w-11 rounded-xl bg-primary/10 border border-primary/30 inline-flex items-center justify-center text-primary shrink-0 group-hover:rotate-6 transition-transform">
                                            <Icon className="h-5 w-5" />
                                        </span>
                                        <div>
                                            <div className="font-mono text-[10px] tracking-[0.22em] text-muted-foreground">
                                                PILLAR {String(i + 1).padStart(2, '0')}
                                            </div>
                                            <h3 className="font-display mt-1 text-xl font-semibold">{p.title}</h3>
                                            <p className="mt-2 text-sm text-foreground/80 leading-relaxed">{p.body}</p>
                                        </div>
                                    </div>
                                </motion.article>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Values strip */}
            <section className="py-10">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
                        {VALUES.map((it) => <ValueCard key={it.key} it={it} />)}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-20">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="dt-neon-border">
                        <div className="dt-neon-inner rounded-[var(--radius)] p-8 sm:p-12 grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
                            <div className="lg:col-span-8">
                                <h3 className="font-display text-2xl sm:text-3xl font-semibold">
                                    Build the future with a partner that combines <span className="dt-gradient-text">vision and execution</span>.
                                </h3>
                                <p className="text-foreground/80 mt-2">
                                    Speak directly with a senior technology specialist about your objectives, constraints, and next steps.
                                </p>
                            </div>
                            <div className="lg:col-span-4 flex">
                                <Button
                                    asChild
                                    size="lg"
                                    className="w-full lg:w-auto bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_0_1px_rgba(0,229,255,0.35),0_0_24px_rgba(0,229,255,0.18)]"
                                    data-testid="mission-cta-button"
                                >
                                    <Link to="/contact" className="inline-flex items-center gap-1.5">
                                        Book a Consultation <ArrowUpRight className="h-4 w-4" />
                                    </Link>
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
